import React, { useEffect, useState, memo, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { ShippingServiceViewModel } from "../../../Types/ExportTypes";
import { CountryAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import DefaultActions from "../../../components/Card/DefaultActions";
import LocalizedNumberInputs from "../../../components/Input/LocalizedNumberInputs"
import ShippingServiceJsonList from "../../../components/ShippingService/ShippingServiceJsonList";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const ShippingServiceEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { agentCode = "", code = "" } = params || {};
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(ShippingServiceViewModel) | null>(null);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  const [showIntegrationSettings, setShowIntegrationSettings] = useState(false);
  const [showShipmentInformation, setShowShipmentInformation] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);

  const handleClickServices = () => {
    setIsServicesModalOpen(true);
  };

  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (code) {
      setIsLoading(true);
      authService
        .get<ShippingServiceViewModel>(`/settingsApi/GetShippingService?agentCode=${agentCode}&code=${code}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultModel: ShippingServiceViewModel & { originalValues: Partial<ShippingServiceViewModel>; hasChanges: boolean } = {
        createNew: true,
        agentCode: agentCode,
        originalValues: {},
        hasChanges: false,
      };
      defaultModel.originalValues = { ...defaultModel };
      setViewModel(defaultModel);
    }
  }, [code, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return; // Prevent save if validation fails
    var newItem = viewModel.createNew ?? false;
    setIsLoading(true);
    try {
      const response = await authService.post<ShippingServiceViewModel>("/settingsApi/CreateOrUpdateShippingService", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/shippingservice/edit", params: { agentCode: response.agentCode, code: response.code } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof ShippingServiceViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as ShippingServiceViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const handleCopy = async (newCode?: string) => {

    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyPreliminaryEntity"), "warning");
      return;
    }

    if (newCode && newCode === viewModel?.code) {
      showSnackbar(t("dialog.entityCodeSame"), "warning");
      return;
    }
    // If newCode is provided, update viewModel and continue
    if (newCode) {
      setViewModel(prev => ({ ...prev, newCode }));
    }


    // If newCode isn't set, open the modal and return
    if (!viewModel?.newCode && !newCode) {
      setIsCopyModalOpen(true);
      return;
    }
    setIsCopyModalOpen(false);
    if (viewModel?.hasChanges) showHasChangesError();
    setIsLoading(true);
    try {
      var response = await authService.post<ShippingServiceViewModel>(`/settingsApi/CopyShippingService`, { ...viewModel, newCode });
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.entityCopied"), "success");


      navigate({ path: "/settings/shippingservice/edit", params: { agentCode: response.agentCode, code: response.code } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.copyFailed")} : ${error.response?.data ?? error.message}`, "error");
      setViewModel(prev => ({ ...prev, newCode: null }));

    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (viewModel?.hasChanges) showHasChangesError();

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/shippingservice/list", params: { agentCode: agentCode } });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteShippingService`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");

      navigate({ path: "/settings/shippingservice/list", params: { agentCode: agentCode } });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }


    if (!viewModel.code || viewModel.code.trim() === "") {
      showSnackbar(t("help.codeRequired"), "warning");
      return false;
    }


    if (!viewModel.description || viewModel.description.trim() === "") {
      showSnackbar(t("help.descriptionRequired"), "warning");
      return false;
    }



    return true;
  };

  const ServicesModal = () => (
    <Dialog open={isServicesModalOpen} onClose={() => setIsServicesModalOpen(false)} maxWidth="md" fullWidth>

      <DialogContent>
        <ShippingServiceJsonList params={{ serviceId: viewModel?.id ?? 0 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsServicesModalOpen(false)} color="primary">{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );

  type CodeInputModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (newCode: string) => void;
    ViewModel?: ShippingServiceViewModel | null
  };
  const CodeInputModal = memo(({ open, onClose, onConfirm, ViewModel }: CodeInputModalProps) => {
    const [tempCode, setTempCode] = useState("");
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t("dialog.enterNewCode")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.newCode")}
            fullWidth
            variant="outlined"
            value={tempCode}
            onChange={(e) => setTempCode(e.target.value)} // Only updates local state
            error={ViewModel?.code === tempCode}
            helperText={ViewModel?.code === tempCode ? t("common.entityCodeSame") : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (!tempCode.trim()) {
                showSnackbar(t("help.codeRequired"), "warning")
                return;
              }
              onConfirm(tempCode?.trim()); // Pass only on confirm
            }}
            color="primary"
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });


  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.shippingService")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onCopy={handleCopy}
            onDelete={handleDelete}

          />
        </Box>
        <CodeInputModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} ViewModel={viewModel} />
        {viewModel && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("common.code")}
                  value={viewModel.code || ""}
                  onChange={(e) => handleChange("code", e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={viewModel.createNew != true}
                  error={!viewModel.code || viewModel.code.trim() === ""}
                  helperText={!viewModel.code || viewModel.code.trim() === "" ? t("help.codeRequired") : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t("common.description")}
                  value={viewModel.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                  fullWidth
                  margin="normal"
                  error={!viewModel.description || viewModel.description.trim() === ""}
                  helperText={!viewModel.description || viewModel.description.trim() === "" ? t("help.descriptionRequired") : ""}
                />
              </Grid>

              {viewModel.createNew !== true && (
                <Grid item xs={12}>
                  <Button variant="contained" onClick={handleClickServices} size="small">
                    JSON
                  </Button>
                </Grid>
              )}

              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.blocked} onChange={(e) => handleChange("blocked", e.target.checked)} />}
                  label={t("common.blocked")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.clickAndCollect} onChange={(e) => handleChange("clickAndCollect", e.target.checked)} />}
                  label={t("common.clickAndCollect")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.returnAsNormalShipment} onChange={(e) => handleChange("returnAsNormalShipment", e.target.checked)} />}
                  label={t("common.returnAsNormalShipment")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.createReturnDocument} onChange={(e) => handleChange("createReturnDocument", e.target.checked)} />}
                  label={t("common.createReturnDocument")}
                />
              </Grid>

            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showShipmentInformation} onChange={(e) => setShowShipmentInformation(e.target.checked)} />}
                label={t("common.showFreightDimensions")}
              />
            </Grid>

            {/* Settings Section */}
            {showShipmentInformation && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >


                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.standardWeight")}
                        value={viewModel.weight ?? null}
                        onValueChange={(value) => {
                          handleChange("weight", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>



                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.width") + " (m)"}
                        value={viewModel.width ?? null}
                        onValueChange={(value) => {
                          handleChange("width", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>


                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.height") + " (m)"}
                        value={viewModel.height ?? null}
                        onValueChange={(value) => {
                          handleChange("height", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>

                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.length") + " (m)"}
                        value={viewModel.length ?? null}
                        onValueChange={(value) => {
                          handleChange("length", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>

                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.minWeight") + " (g)"}
                        value={viewModel.minWeight ?? null}
                        onValueChange={(value) => {
                          handleChange("minWeight", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>


                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.maxWeight") + " (g)"}
                        value={viewModel.maxWeight ?? null}
                        onValueChange={(value) => {
                          handleChange("maxWeight", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>


                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.maxLength") + " (cm)"}
                        value={viewModel.maxLength ?? null}
                        onValueChange={(value) => {
                          handleChange("maxLength", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>


                  <Grid item xs={12}>
                    <FormControl focused className="col " variant="outlined">

                      <LocalizedNumberInputs
                        label={t("common.maxVolume") + " (cm³)"}
                        value={viewModel.maxVolume ?? null}
                        onValueChange={(value) => {
                          handleChange("maxVolume", value ?? null);
                        }}

                        allowNegative={false} // Prevents negative numbers
                        decimalScale={4} // Ensures max 4 decimals
                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                      />
                    </FormControl >

                  </Grid>
                </Box>
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showIntegrationSettings} onChange={(e) => setShowIntegrationSettings(e.target.checked)} />}
                label={t("common.showShipmentInfo")}
              />
            </Grid>

            {/* Settings Section */}
            {showIntegrationSettings && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >

                  <Grid item xs={6}>
                    <FormControlLabel
                      control={<Checkbox checked={!!viewModel.useShippingAddress} onChange={(e) => handleChange("useShippingAddress", e.target.checked)} />}
                      label={t("common.useShipmentAddress")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.name")}
                      value={viewModel.shipToName || ""}
                      onChange={(e) => handleChange("shipToName", e.target.value)}
                      fullWidth
                      margin="normal"
                      disabled={viewModel.useShippingAddress != true}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.name2")}
                      value={viewModel.shipToName2 || ""}
                      onChange={(e) => handleChange("shipToName2", e.target.value)}
                      fullWidth
                      margin="normal"
                      disabled={viewModel.useShippingAddress != true}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.address")}
                      value={viewModel.shipToAddress || ""}
                      onChange={(e) => handleChange("shipToAddress", e.target.value)}
                      fullWidth
                      margin="normal"
                      disabled={viewModel.useShippingAddress != true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.address2")}
                      value={viewModel.shipToAddress2 || ""}
                      onChange={(e) => handleChange("shipToAddress2", e.target.value)}
                      fullWidth
                      margin="normal"
                      disabled={viewModel.useShippingAddress != true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Postcode"
                      value={viewModel.shipToPostCode || ""}
                      onChange={(e) => handleChange("shipToPostCode", e.target.value)}
                      fullWidth
                      margin="normal"
                      disabled={viewModel.useShippingAddress != true}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.city")}
                      value={viewModel.shipToCity || ""}
                      onChange={(e) => handleChange("shipToCity", e.target.value)}
                      fullWidth
                      margin="normal"
                      disabled={viewModel.useShippingAddress != true}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <CountryAutoComplete
                        key={viewModel.shipToCountryCode || undefined}
                        valueId={viewModel.shipToCountryCode || null}
                        onChange={(selected) => handleChange("shipToCountryCode", selected?.value || "")}
                        disabled={viewModel.useShippingAddress != true}
                      />
                    </FormControl>
                  </Grid>


                </Box>
              </>
            )}
          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={handleCancelChanges} disabled={!viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.cancel")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>

      </CenteredCard>


      <ServicesModal />
    </>
  );
};

export default ShippingServiceEditPage;
